import { useState } from "react";
import { Search, Loader2 } from "lucide-react";
import api from "@/lib/api";
import CommandCenterView from "./CommandCenterView";
import NetworkMap from "./NetworkMap";
import ProfessionalProfile from "./ProfessionalProfile";

const NET_META = {
  attorney: { label: "Attorney Command", accent: "text-cobalt" },
  rn: { label: "RN Command", accent: "text-rose-600" },
  telehealth: { label: "Telehealth Command", accent: "text-emerald-600" },
  drone: { label: "Drone Command", accent: "text-amber-600" },
};

export default function NetworkCommand({ view, onNavigate }) {
  if (view === "search") return <GlobalSearch />;
  if (view === "map") return <NetworkMap />;
  return <CommandCenterView network={view} meta={NET_META[view]} onNavigate={onNavigate} />;
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null); // {network, id}

  const run = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get("/network-command/search", { params: { q } });
      setResults(data);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy">Global Network Search</h3>
        <p className="text-sm text-slate-500 mt-1">Instantly locate any professional across all four networks — by name, phone, email, organization, license, or state.</p>
        <form onSubmit={run} className="mt-4 flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nc-global-search-input"
              placeholder="Search name, email, phone, license, state, organization…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30" />
          </div>
          <button type="submit" data-testid="nc-global-search-btn"
            className="px-5 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </button>
        </form>
      </div>

      {results && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="text-sm text-slate-500" data-testid="nc-search-count">{results.count} result{results.count === 1 ? "" : "s"} for “{results.query}”</div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Network</th><th className="py-2 pr-4">Organization</th>
                <th className="py-2 pr-4">State</th><th className="py-2 pr-4">License</th><th className="py-2 pr-4">Status</th>
              </tr></thead>
              <tbody>
                {results.results.map((r) => (
                  <tr key={`${r.network}-${r.id}`} onClick={() => setOpen({ network: r.network, id: r.id })}
                    data-testid={`nc-search-row-${r.id}`}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                    <td className="py-2.5 pr-4 font-medium text-navy">{r.name || "—"}</td>
                    <td className="py-2.5 pr-4">{r.network_label}</td>
                    <td className="py-2.5 pr-4">{r.organization || "—"}</td>
                    <td className="py-2.5 pr-4">{r.state || "—"}</td>
                    <td className="py-2.5 pr-4">{r.license || "—"}</td>
                    <td className="py-2.5 pr-4"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {results.results.length === 0 && <div className="py-8 text-center text-sm text-slate-400">No professionals matched.</div>}
          </div>
        </div>
      )}
      {open && <ProfessionalProfile network={open.network} memberId={open.id} onClose={() => setOpen(null)} />}
    </div>
  );
}

export const STATUS_STYLES = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  denied: "bg-red-50 text-red-700 border-red-200",
  suspended: "bg-orange-50 text-orange-700 border-orange-200",
  inactive: "bg-slate-50 text-slate-500 border-slate-200",
  archived: "bg-slate-100 text-slate-400 border-slate-200",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.inactive}`}>
      {(status || "unknown").toUpperCase()}
    </span>
  );
}
